import isFalsy from '../helpers/is-falsy';
import isTruthy from '../helpers/is-truthy';
import { parseDate } from '../helpers/parse-date';

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

const minAmount = 0.01;
const maxAmount = 99999999999;

function isValidAmount(value) {
  if (isBlank(value)) return false;
  const amount = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(amount) && amount >= minAmount && amount <= maxAmount;
}

const purchaseOrders = {
  'Client Name': {
    requirementType: 'required',
    description:
      'The name of the client the purchase order belongs to. The name matching is strict and must be accurate on capitalization, spacing, and punctuation. Rows whose client is not found are skipped.',
    dataType: 'string',
    validator: (value) => !isBlank(value) && String(value).trim().length <= 255,
  },
  'PO Name': {
    requirementType: 'required',
    description: 'Name of the purchase order (max 255 characters).',
    dataType: 'string',
    validator: (value) => !isBlank(value) && String(value).trim().length <= 255,
  },
  'PO Number': {
    requirementType: 'required',
    description:
      'The purchase order number (max 255 characters). A purchase order with the same number on the same client will be skipped, so this is also the duplicate check for re-imports.',
    dataType: 'string',
    validator: (value) => !isBlank(value) && String(value).trim().length <= 255,
  },
  'PO Amount': {
    requirementType: 'required',
    description:
      'The total value of the purchase order. Must be at least 0.01 and no more than 99,999,999,999. Currency symbols and thousand separators are accepted.',
    dataType: 'number',
    validator: isValidAmount,
  },
  Currency: {
    requirementType: 'optional',
    description:
      "Provide a valid 3-letter ISO currency code (for example: USD, EUR, GBP, AUD, BRL). Leave blank to use the client's currency. Workspaces without the multicurrency feature must use the workspace currency.",
    dataType: 'string',
    validator: (value) => isBlank(value) || /^[A-Z]{3}$/.test(String(value).trim().toUpperCase()),
  },
  'Amount Includes Tax': {
    requirementType: 'optional',
    description:
      'Provide "Yes" (or "Y", "True", "1") if the purchase order amount is inclusive of taxes, meaning invoice taxes count against the remaining amount. Defaults to "Yes" when left blank.',
    dataType: 'boolean',
    validator: (value) => isBlank(value) || isTruthy(value) || isFalsy(value),
  },
  'Start Date': {
    requirementType: 'optional',
    description: 'The date the purchase order takes effect. Use the "YYYY-MM-DD" or "MM/DD/YYYY" format.',
    dataType: 'date',
    validator: (value) => isBlank(value) || !!parseDate(String(value).trim()),
  },
  'End Date': {
    requirementType: 'optional',
    description:
      'The date the purchase order expires. Use the "YYYY-MM-DD" or "MM/DD/YYYY" format. Must not be earlier than the Start Date.',
    dataType: 'date',
    validator: (value, row) => {
      if (isBlank(value)) return true;
      const end = parseDate(String(value).trim());
      if (!end) return false;
      const start = isBlank(row?.['Start Date']) ? null : parseDate(String(row['Start Date']).trim());
      return !start || end >= start;
    },
  },
  Notes: {
    requirementType: 'optional',
    description: 'Notes related to the purchase order (max 5000 characters).',
    dataType: 'string',
    validator: (value) => isBlank(value) || String(value).length <= 5000,
  },
};

export default purchaseOrders;
